import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { trpc } from "../lib/trpc.js";
import { useAuth } from "../features/auth/useAuth.js";
import { ROTULO_PAPEL } from "@foto/shared";
import { Botao, Selo, cx } from "./ui.js";

/**
 * Casca da aplicação.
 *
 * No celular a navegação é uma gaveta: a tela inteira pertence ao trabalho do
 * operador, e o menu só existe quando ele o pede. Em telas largas a mesma lista
 * fica fixa à esquerda, sem duplicar a marcação.
 */

interface ItemNav {
  para: string;
  rotulo: string;
  admin?: boolean;
}

const OPERACAO: ItemNav[] = [
  { para: "/nova", rotulo: "Nova foto" },
  { para: "/lotes", rotulo: "Lotes" },
  { para: "/biblioteca", rotulo: "Biblioteca" },
  { para: "/impressao", rotulo: "Impressão" },
  { para: "/marcadores", rotulo: "Marcadores" },
];

const ACERVO: ItemNav[] = [
  { para: "/banco-imagens", rotulo: "Banco de imagens" },
  { para: "/banco-capas", rotulo: "Capas de livro" },
  { para: "/divulgacoes", rotulo: "Divulgações" },
  { para: "/figurinhas", rotulo: "Figurinhas" },
  { para: "/musicas", rotulo: "Músicas" },
  { para: "/modelos-de-marcador", rotulo: "Modelos de marcador" },
];

/**
 * A conta em si: o que pertence a quem contratou, não a quem opera.
 *
 * Fica fora de "Atendimento" porque um operador contratado não precisa disso —
 * e `kiosk.meu` recusaria a consulta dele, deixando um item de menu que só
 * mostra erro.
 */
const CONTA: ItemNav[] = [
  { para: "/pacote", rotulo: "Meu pacote" },
  { para: "/comprar", rotulo: "Comprar fotos" },
  { para: "/kiosk", rotulo: "Kiosk" },
];

const ADMIN: ItemNav[] = [
  { para: "/admin/vivencias", rotulo: "Vivências", admin: true },
  { para: "/admin/instituicoes", rotulo: "Locais/Eventos", admin: true },
  { para: "/admin/participantes", rotulo: "Participantes", admin: true },
  { para: "/admin/pacotes", rotulo: "Pacotes", admin: true },
  { para: "/admin/pagamentos", rotulo: "Pagamentos", admin: true },
  { para: "/admin/custos", rotulo: "Custos de IA", admin: true },
  { para: "/admin/usuarios", rotulo: "Usuários", admin: true },
  { para: "/admin/config", rotulo: "Configurações", admin: true },
];

export function AppShell({ children }: { children: ReactNode }) {
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const { pathname } = useLocation();

  // Navegar sempre fecha a gaveta; deixá-la aberta esconderia o resultado.
  useEffect(() => setGavetaAberta(false), [pathname]);

  return (
    <div className="flex min-h-full">
      {/* `sticky` com altura de tela: sem isso a barra crescia junto com o
          conteúdo, e numa página longa como a biblioteca o rodapé com o "Sair"
          ia parar lá embaixo, fora da vista. A rolagem interna é dela. */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 overflow-y-auto border-r border-borda bg-superficie/40 lg:block">
        <Navegacao />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="area-segura-superior sticky top-0 z-20 flex items-center gap-3 border-b border-borda bg-fundo/95 px-4 pb-3 backdrop-blur lg:hidden">
          <button
            onClick={() => setGavetaAberta(true)}
            aria-label="Abrir menu"
            className="-ml-2 flex size-11 items-center justify-center rounded-peca text-texto-suave hover:bg-superficie"
          >
            <svg
              viewBox="0 0 20 20"
              className="size-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
            >
              <path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round" />
            </svg>
          </button>
          <span className="font-display text-[1.0625rem] tracking-[-0.01em]">Foto Interativa</span>

          {/* No celular a barra lateral é gaveta, e quem está logado ficava
              escondido atrás do menu — junto com a saída. Num aparelho que passa
              de mão em mão durante o evento, saber de quem é a sessão precisa
              estar à vista. */}
          <Conta className="ml-auto" />
        </header>

        <main className="min-w-0 flex-1">{children}</main>
      </div>

      {gavetaAberta && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            aria-label="Fechar menu"
            onClick={() => setGavetaAberta(false)}
            className="absolute inset-0 bg-black/70"
          />
          <div className="absolute inset-y-0 left-0 w-[17rem] max-w-[85vw] border-r border-borda bg-superficie shadow-2xl shadow-black/60">
            <Navegacao aoNavegar={() => setGavetaAberta(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Encerra a sessão e recarrega a aplicação.
 *
 * Tentei antes limpar o cache do React Query e marcar `auth.me` como nulo. A
 * sessão morria no servidor, mas a tela continuava mostrando o usuário: limpar
 * o cache por baixo de componentes montados que dependem dele derrubava a
 * árvore em vez de levá-la ao login.
 *
 * Recarregar resolve e é mais correto para este uso: o aparelho passa de mão em
 * mão durante o evento, e uma recarga garante que nada do operador anterior
 * sobrou em memória — nem cache, nem estado de tela, nem foto carregada.
 *
 * `onSettled` e não `onSuccess`: se o servidor recusar, a sessão local vai
 * embora do mesmo jeito. Manter alguém "logado" numa tela que já não responde é
 * pior do que deslogar demais.
 */
function useSair() {
  return trpc.auth.logout.useMutation({
    onSettled: () => window.location.assign("/"),
  });
}

/** As iniciais do nome, para caber num alvo de toque sem virar texto. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? "?";
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? "") : "";
  return (primeira + ultima).toUpperCase();
}

/**
 * Quem está usando o sistema, e como sair.
 *
 * Abre um painel em vez de mostrar tudo na barra: no celular não há largura para
 * nome, papel e botão, e o que importa ver de relance é só "esta sessão é de
 * alguém, e é este alguém".
 */
function Conta({ className }: { className?: string }) {
  const { usuario } = useAuth();
  const [aberto, setAberto] = useState(false);
  const sair = useSair();

  if (!usuario) return null;

  return (
    <div className={cx("relative", className)}>
      <button
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-label={`Conta de ${usuario.name}`}
        className={cx(
          "flex size-11 items-center justify-center rounded-full",
          "text-[0.8125rem] font-semibold transition-colors",
          aberto ? "bg-brasa text-fundo" : "bg-superficie-alta text-texto-suave hover:text-texto",
        )}
      >
        {iniciais(usuario.name)}
      </button>

      {aberto && (
        <>
          {/* Toque fora fecha. Sem isto o painel só sairia da tela pelo próprio
              botão, e ninguém procura o mesmo botão para desfazer. */}
          <button
            aria-label="Fechar"
            onClick={() => setAberto(false)}
            className="fixed inset-0 z-30 cursor-default"
          />
          <div className="absolute right-0 z-40 mt-2 w-56 rounded-peca border border-borda bg-superficie p-3 shadow-2xl shadow-black/60">
            <p className="text-[0.9375rem] leading-tight text-texto">{usuario.name}</p>
            <p className="mt-0.5 font-mono text-[0.75rem] break-all text-texto-fraco">
              {usuario.email}
            </p>
            <div className="mt-2">
              <Selo tom="quieto">{ROTULO_PAPEL[usuario.role]}</Selo>
            </div>
            <Botao
              tom="quieto"
              largo
              onClick={() => sair.mutate()}
              carregando={sair.isPending}
              className="mt-3"
            >
              Sair
            </Botao>
          </div>
        </>
      )}
    </div>
  );
}

function Navegacao({ aoNavegar }: { aoNavegar?: () => void }) {
  const { usuario, ehAdmin } = useAuth();
  // Quem contratou: o cliente e, para conferência, o admin.
  const ehTitular = usuario?.role === "cliente" || ehAdmin;
  const sair = useSair();

  return (
    <nav className="area-segura-superior flex h-full flex-col gap-6 overflow-y-auto px-3 pb-4">
      <div className="px-2 pt-1">
        <p className="font-display text-[1.25rem] leading-none tracking-[-0.01em]">
          Foto Interativa
        </p>
      </div>

      <Grupo titulo="Atendimento" itens={OPERACAO} aoNavegar={aoNavegar} />
      <Grupo titulo="Acervo" itens={ACERVO} aoNavegar={aoNavegar} />
      {ehTitular && <Grupo titulo="Conta" itens={CONTA} aoNavegar={aoNavegar} />}
      {ehAdmin && <Grupo titulo="Administração" itens={ADMIN} aoNavegar={aoNavegar} />}

      <div className="mt-auto border-t border-borda px-2 pt-4">
        <p className="text-[0.8125rem] text-texto-suave">{usuario?.name}</p>
        <p className="font-mono text-[0.75rem] break-all text-texto-fraco">{usuario?.email}</p>
        <p className="mt-0.5 text-[0.75rem] text-texto-fraco">
          {usuario ? ROTULO_PAPEL[usuario.role] : ""}
        </p>
        <Botao
          tom="fantasma"
          onClick={() => sair.mutate()}
          formato="nu"
          carregando={sair.isPending}
          className="mt-2"
        >
          Sair
        </Botao>
      </div>
    </nav>
  );
}

function Grupo({
  titulo,
  itens,
  aoNavegar,
}: {
  titulo: string;
  itens: ItemNav[];
  aoNavegar?: () => void;
}) {
  return (
    <div>
      <p className="mb-1.5 px-2 text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-texto-fraco">
        {titulo}
      </p>
      <ul className="flex flex-col gap-0.5">
        {itens.map((item) => (
          <li key={item.para}>
            <NavLink
              to={item.para}
              onClick={aoNavegar}
              className={({ isActive }) =>
                cx(
                  "flex min-h-11 items-center rounded-peca px-2 text-[0.9375rem] transition-colors",
                  isActive
                    ? "bg-brasa-fraca font-medium text-brasa"
                    : "text-texto-suave hover:bg-superficie hover:text-texto",
                )
              }
            >
              {item.rotulo}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
